import { useEffect, useState } from 'react';
import './CustomScale.css';

const CustomScale = ({ mapRef }) => {
  const [scaleText, setScaleText] = useState('100 m');
  const [scaleWidth, setScaleWidth] = useState(100);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Check if map exists
    const checkMap = () => {
      if (mapRef?.current) {
        setIsReady(true);
        return true;
      }
      return false;
    };

    // Try immediately
    if (checkMap()) return;

    // If not ready, poll for map ref
    const interval = setInterval(() => {
      if (checkMap()) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [mapRef]);

  useEffect(() => {
    if (!isReady || !mapRef?.current) return;

    const map = mapRef.current;

    const updateScale = () => {
      try {
        // Safety checks
        if (!map || !map.getContainer) return;

        const container = map.getContainer();
        if (!container || container.clientHeight === 0) return;

        const y = container.clientHeight / 2;
        
        // Get screen width for responsive max width
        const screenWidth = window.innerWidth || 1024;
        const maxWidth = screenWidth < 480 ? 80 : 
                        screenWidth < 768 ? 100 : 
                        screenWidth < 1024 ? 120 : 150;

        // Get two points: left edge and maxWidth pixels to the right
        const left = map.unproject([0, y]);
        const right = map.unproject([maxWidth, y]);

        if (!left || !right || !left.lat || !right.lat) return;

        // Calculate distance using Haversine formula
        const distance = getDistance(
          left.lat, left.lng,
          right.lat, right.lng
        );

        if (!distance || distance <= 0 || !isFinite(distance)) return;

        // Get nice round number
        const { value, unit } = getNiceDistance(distance);
        
        // Calculate actual width for the nice round number
        const actualDistance = unit === 'km' ? value * 1000 : value;
        const width = Math.round((actualDistance / distance) * maxWidth);

        if (!isFinite(width) || width <= 0) return;

        setScaleText(`${value} ${unit}`);
        setScaleWidth(width);
      } catch (err) {
      }
    };

    // Setup listeners
    const setup = () => {
      updateScale();
      map.on('zoom', updateScale);
      map.on('move', updateScale);
      map.on('zoomend', updateScale);
      map.on('moveend', updateScale);
    };

    // Wait for map to be ready
    if (map.loaded && map.loaded()) {
      setup();
    } else {
      map.once('load', setup);
    }

    // Window resize handler with debounce
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateScale, 100);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(resizeTimeout);
      if (map && map.off) {
        map.off('zoom', updateScale);
        map.off('move', updateScale);
        map.off('zoomend', updateScale);
        map.off('moveend', updateScale);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [isReady, mapRef]);

  return (
    <div className="custom-scale-control">
      <div className="custom-scale-label">{scaleText}</div>
      <div className="custom-scale-bar" style={{ width: `${scaleWidth}px` }}></div>
    </div>
  );
};

// Haversine formula to calculate distance between two lat/lng points
function getDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000; // Earth radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Convert distance to nice round numbers
function getNiceDistance(meters) {
  if (meters >= 1000) {
    const km = meters / 1000;
    if (km >= 100) return { value: Math.round(km / 100) * 100, unit: 'km' };
    if (km >= 50) return { value: 50, unit: 'km' };
    if (km >= 20) return { value: 20, unit: 'km' };
    if (km >= 10) return { value: 10, unit: 'km' };
    if (km >= 5) return { value: 5, unit: 'km' };
    if (km >= 2) return { value: 2, unit: 'km' };
    if (km >= 1) return { value: 1, unit: 'km' };
    return { value: 500, unit: 'm' };
  }
  
  if (meters >= 500) return { value: 500, unit: 'm' };
  if (meters >= 200) return { value: 200, unit: 'm' };
  if (meters >= 100) return { value: 100, unit: 'm' };
  if (meters >= 50) return { value: 50, unit: 'm' };
  if (meters >= 20) return { value: 20, unit: 'm' };
  if (meters >= 10) return { value: 10, unit: 'm' };
  if (meters >= 5) return { value: 5, unit: 'm' };
  if (meters >= 2) return { value: 2, unit: 'm' };
  return { value: 1, unit: 'm' };
}

export default CustomScale;
